import React, { useState } from 'react';
import { Plus, Edit2, X, Megaphone } from 'lucide-react';
import { Card, SectionHeader, Btn, Badge, Modal, Field, Input, EmptyState } from '../components/UI';
import { nowPKTDate } from '../utils/auth';

const TYPE_OPTIONS = [
  { value: 'announcement', label: 'Announcement', color: 'blue' },
  { value: 'policy',       label: 'Policy update', color: 'purple' },
  { value: 'reminder',     label: 'Reminder',      color: 'yellow' },
  { value: 'urgent',       label: 'Urgent',        color: 'red' },
];

function typeColor(type) {
  const t = TYPE_OPTIONS.find(o => o.value === type);
  return t ? t.color : 'gray';
}
function typeLabel(type) {
  const t = TYPE_OPTIONS.find(o => o.value === type);
  return t ? t.label : type;
}
function typeBar(type) {
  return { announcement:'var(--primary)', policy:'var(--accent)', reminder:'var(--warning,#C79A5B)', urgent:'var(--red)' }[type] || 'var(--border2)';
}

const inputSt = {
  width: '100%', padding: '9px 12px', fontSize: 13,
  background: 'var(--bg3)', border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)', color: 'var(--text)', boxSizing: 'border-box',
};

export default function UpdatesPage({ store, persist, showToast }) {
  const updates = store.updates || [];

  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null); // update object or null
  const [form,      setForm]      = useState({ title: '', body: '', type: 'announcement' });

  function openAdd() {
    setEditing(null);
    setForm({ title: '', body: '', type: 'announcement' });
    setShowModal(true);
  }

  function openEdit(u) {
    setEditing(u);
    setForm({ title: u.title, body: u.body, type: u.type });
    setShowModal(true);
  }

  function save() {
    if (!form.title.trim() || !form.body.trim()) {
      showToast('Title and body are required.', 'error');
      return;
    }
    let newUpdates;
    if (editing) {
      newUpdates = updates.map(u => u.id === editing.id
        ? { ...u, title: form.title.trim(), body: form.body.trim(), type: form.type, editedAt: new Date().toISOString() }
        : u
      );
      showToast('Update edited.');
    } else {
      const entry = {
        id:        Date.now().toString(),
        title:     form.title.trim(),
        body:      form.body.trim(),
        type:      form.type,
        createdAt: new Date().toISOString(),
        date:      nowPKTDate(),
      };
      newUpdates = [entry, ...updates];
      showToast('Update posted.');
    }
    persist({ ...store, updates: newUpdates });
    setShowModal(false);
  }

  function remove(id) {
    if (!window.confirm('Delete this update?')) return;
    persist({ ...store, updates: updates.filter(u => u.id !== id) }, { deletedIds: { updates: [id] } });
    showToast('Update deleted.');
  }

  const f = key => e => setForm(p => ({ ...p, [key]: e.target.value }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
        <div>
          <h1 className="t-h1">Updates & Announcements</h1>
          <p className="t-sub" style={{ marginTop:4 }}>Company-wide communications, newest first.</p>
        </div>
        <Btn variant="primary" onClick={openAdd}><Plus size={15} /> Post Update</Btn>
      </div>

      {updates.length === 0
        ? <Card><EmptyState message="No updates posted yet. Post one to notify all employees." /></Card>
        : <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {updates.map(u => (
              <Card key={u.id} style={{ padding:'20px 24px', borderLeft:`4px solid ${typeBar(u.type)}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10, flexWrap:'wrap' }}>
                  <Badge label={typeLabel(u.type)} color={typeColor(u.type)} />
                  <span style={{ fontSize:12.5, color:'var(--text3)', fontFamily:'var(--mono)', fontVariantNumeric:'lining-nums tabular-nums' }}>
                    {new Date(u.createdAt).toLocaleDateString('default', { day:'numeric', month:'short', year:'numeric' })}
                  </span>
                  {u.editedAt && <span style={{ fontSize:11, color:'var(--text3)' }}>· edited</span>}
                  <div style={{ flex:1 }} />
                  <Btn size="sm" variant="ghost" onClick={() => openEdit(u)}><Edit2 size={13} /></Btn>
                  <Btn size="sm" variant="danger" onClick={() => remove(u.id)}><X size={13} /></Btn>
                </div>
                <div style={{ fontSize:18, fontFamily:'var(--serif)', fontWeight:400, color:'var(--text)', marginBottom:8, letterSpacing:'-0.01em' }}>{u.title}</div>
                <p style={{ fontSize:14.5, color:'var(--text2)', margin:0, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{u.body}</p>
              </Card>
            ))}
          </div>
      }

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit update' : 'Post company update'}>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 400, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Type</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TYPE_OPTIONS.map(o => (
              <button key={o.value} onClick={() => setForm(p => ({ ...p, type: o.value }))} style={{
                fontSize: 12, fontWeight: 500, padding: '5px 12px', borderRadius: 'var(--radius)', cursor: 'pointer',
                border: form.type === o.value ? '1.5px solid var(--accent)' : '1px solid var(--border2)',
                background: form.type === o.value ? 'rgba(13,148,136,0.1)' : 'var(--bg3)',
                color: form.type === o.value ? 'var(--accent)' : 'var(--text3)',
              }}>{o.label}</button>
            ))}
          </div>
        </div>
        <Field label="Title">
          <Input value={form.title} onChange={f('title')} placeholder="e.g. New office hours from Monday" autoFocus />
        </Field>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 400, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Message</label>
          <textarea
            value={form.body}
            onChange={f('body')}
            placeholder="Write your update here…"
            rows={5}
            style={{ ...inputSt, resize: 'vertical', fontFamily: 'var(--font)', lineHeight: 1.5 }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn onClick={() => setShowModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={save}>{editing ? 'Save changes' : 'Post update'}</Btn>
        </div>
      </Modal>
    </div>
  );
}
